(() => {
  if (customElements.get('cp-buybox-gifts')) return;

  const normalizeId = (id) => String(id || '').split('/').pop();
  const path = (value) => new URL(value, location.origin).pathname.replace(/\/$/, '');
  const isGift = (item) => Boolean(item.properties?.__is_free_gift || item.properties?._free_gift);
  const offerIds = (item) => String(item.properties?.__page_offer_ids || item.properties?._da_page_offer_ids || '').split(',').filter(Boolean);
  let configPromise;
  const getConfig = () => configPromise ||= fetch(`/apps/everything-app?shop=${encodeURIComponent(window.Shopify?.shop || '')}`, {
    credentials: 'same-origin', headers: { Accept: 'application/json' },
  }).then((response) => {
    if (!response.ok) throw new Error('Gift offers could not be loaded. Please try again.');
    return response.json();
  }).then((payload) => payload.config || {});
  const getCart = () => fetch(`${window.Shopify?.routes?.root || '/'}cart.js?buybox_gifts=${Date.now()}`, { cache: 'no-store' }).then((response) => {
    if (!response.ok) throw new Error('Your cart could not be checked for gifts. Please try again.');
    return response.json();
  });

  function giftChoices(offer) {
    return (offer?.tiers || []).flatMap((tier) => {
      const gift = tier.rewards?.freeGift;
      const choices = (gift?.choices || []).filter((choice) => choice.storefrontAvailable !== false);
      return Array.from({ length: choices.length ? Number(gift.quantity || 1) : 0 }, (_, index) => ({
        ...choices[index % choices.length], minQuantity: Number(tier.minQuantity),
        variantId: normalizeId(choices[index % choices.length].variantId || choices[index % choices.length].id),
      }));
    });
  }

  class BuyboxGifts extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.relocating) return;
      this.root = this.closest('product-info') || this.closest('.shopify-section');
      this.setupPlacement();
      this.subject = JSON.parse(this.querySelector('[data-gift-product]').textContent);
      this.ready = this.initialize();
      this.onChange = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.render());
      };
      this.root.addEventListener('input', this.onChange);
      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('click', this.onChange);
      this.observer = new MutationObserver(this.onChange);
      this.observer.observe(this.root, { subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'value', 'data-buybox-meal-count', 'data-revlift-bundle-meal-count'] });
      this.onCart = () => getCart().then((cart) => { this.cart = cart; this.render(); }).catch(() => {});
      document.addEventListener('cart:updated', this.onCart);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCart);
      }
    }

    disconnectedCallback() {
      if (this.relocating) return;
      this.mobileQuery?.removeEventListener('change', this.onPlacementChange);
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      ['input', 'change', 'click'].forEach((event) => this.root?.removeEventListener(event, this.onChange));
      document.removeEventListener('cart:updated', this.onCart);
      this.unsubscribe?.();
      this.ready = null;
    }

    setupPlacement() {
      // Keep one panel and its gift state as it moves between the two layouts.
      if (!this.desktopAnchor?.isConnected) {
        this.desktopAnchor = document.createComment('Buy box gifts desktop position');
        this.before(this.desktopAnchor);
      }
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.onPlacementChange = () => {
        const form = document.getElementById(this.dataset.formId);
        const button = form?.querySelector('button[name="add"], [data-submit-button]');
        let target = button?.closest('.cp-os__submit-wrap, .cp-bob__submit-wrap, .product-form__buttons') || button;
        if (target?.previousElementSibling?.matches('[data-atc-anchor]')) target = target.previousElementSibling;
        const mobile = this.mobileQuery.matches && Boolean(target);
        this.toggleAttribute('data-mobile-placement', mobile);
        this.relocating = true;
        try {
          if (mobile) target.before(this);
          else this.desktopAnchor.after(this);
        } finally {
          this.relocating = false;
        }
      };
      this.mobileQuery.addEventListener('change', this.onPlacementChange);
      this.onPlacementChange();
    }

    async initialize() {
      this.gifts = Array.from(this.querySelectorAll('[data-gift-handle]'), (tile) => ({
        variantId: tile.dataset.variantId, minQuantity: Number(tile.dataset.minQuantity),
      }));
      this.hidden = this.dataset.kind === 'product' && !(this.subject.collectionHandles || []).includes('meals');
      this.render();
      if (!this.hidden && location.hash === `#${this.id}`) this.scrollIntoView({ block: 'start' });
      try {
        const config = await getConfig();
        const offers = (config.pageOffers || []).filter((offer) => giftChoices(offer).length);
        this.offer = offers.find((offer) => (offer.pagePaths || []).some((value) => path(value) === path(location.pathname)))
          || offers.find((offer) => offer.storeWide === true)
          || offers.find((offer) => (offer.pagePaths || []).some((value) => path(value) === '/pages/customer-favorites'));
        if (!this.offer || !this.eligible()) return;
        // Page-specific promotions take precedence over sitewide gifts.
        // The Customer Favorites fallback remains display-only on other pages.
        this.canRedeem = this.offer.storeWide || (this.offer.pagePaths || []).some((value) => path(value) === path(location.pathname));
        this.gifts = giftChoices(this.offer);
        this.cart = await getCart();
        this.querySelectorAll('[data-gift-handle]').forEach((tile) => {
          const gift = this.gifts.find((choice) => choice.productHandle === tile.dataset.giftHandle);
          tile.hidden = !gift;
          if (!gift) return;
          tile.dataset.variantId = gift.variantId;
          tile.querySelector('[data-gift-title]').textContent = gift.productTitle || gift.title;
          tile.querySelector('[data-gift-value]').textContent = new Intl.NumberFormat('en-US', {
            style: 'currency', currency: this.cart.currency || 'USD',
          }).format(Number(gift.price || 0));
        });
        this.hidden = false;
        this.render();
        if (location.hash === `#${this.id}`) this.scrollIntoView({ block: 'start' });
      } catch (error) {
        this.canRedeem = false;
        console.warn('[Buy box gifts]', error.message);
      }
    }

    eligible() {
      if (this.dataset.kind !== 'product') return true;
      const conditions = this.offer.conditions || {};
      return (this.subject.collectionHandles || []).includes('meals')
        || (conditions.collectionItems || []).some((item) => (this.subject.collections || []).map(normalizeId).includes(normalizeId(item.collectionId)))
        || (conditions.items || []).some((item) => normalizeId(item.productId) === String(this.subject.id)
          || (this.subject.variants || []).map(normalizeId).includes(normalizeId(item.variantId)));
    }

    selectedCount() {
      const bundle = this.closest('product-info');
      if (this.dataset.kind === 'bundle') return Number(bundle?.dataset.revliftBundleMealCount || 0);
      if (this.dataset.kind === 'builder') return Number(this.root.querySelector('[data-buybox-meal-count]')?.dataset.buyboxMealCount || 0);
      const form = document.getElementById(this.dataset.formId) || this.root.querySelector('form[action*="/cart/add"]');
      return Math.max(1, Number(form?.elements.namedItem('quantity')?.value || 1));
    }

    cartMealCount(cart) {
      if (!this.offer) return 0;
      return (cart?.items || []).reduce((total, item) => total + (!isGift(item) && offerIds(item).includes(String(this.offer.id)) ? Number(item.quantity) : 0), 0);
    }

    render() {
      if (this.hidden || !this.gifts) return;
      const count = this.cartMealCount(this.cart) + this.selectedCount();
      let unlockedCount = 0;
      this.querySelectorAll('[data-gift-handle]').forEach((tile) => {
        const unlocked = this.gifts.some((gift) => gift.variantId === tile.dataset.variantId && count >= gift.minQuantity);
        tile.dataset.unlocked = String(unlocked);
        tile.querySelector('[data-gift-status]').textContent = unlocked ? 'Unlocked' : 'Locked';
        if (unlocked) unlockedCount += 1;
      });
      const announcement = `${unlockedCount} of ${this.querySelectorAll('[data-gift-handle]:not([hidden])').length} gifts unlocked.`;
      const live = this.querySelector('[data-gift-announcement]');
      if (live.textContent !== announcement) live.textContent = announcement;
    }
  }

  function findPanel(root) {
    const scope = root.closest('product-info') || root.closest('.shopify-section') || root;
    return scope.querySelector('cp-buybox-gifts');
  }

  async function prepareItems(items, root) {
    const panel = findPanel(root);
    if (!panel) return items;
    await panel.ready;
    if (panel.hidden || !panel.offer || !panel.canRedeem) return items;
    const offerId = String(panel.offer.id);
    const pending = items.map((item) => {
      if (isGift(item)) return item;
      const ids = [...new Set([...offerIds(item), offerId])].sort().join(',');
      return { ...item, properties: { ...item.properties, __page_offer_ids: ids, _da_page_offer_ids: ids } };
    });
    const cart = await getCart();
    const count = panel.cartMealCount(cart) + pending.reduce((total, item) => total + (isGift(item) ? 0 : Number(item.quantity || 1)), 0);
    const desired = new Map();
    panel.gifts.filter((gift) => count >= gift.minQuantity).forEach((gift) => desired.set(gift.variantId, (desired.get(gift.variantId) || 0) + 1));
    for (const [id, quantity] of desired) {
      const existing = [...cart.items, ...pending].reduce((total, item) => total + (
        isGift(item) && String(item.properties.__gift_offer_id) === offerId && normalizeId(item.variant_id || item.id) === id ? Number(item.quantity) : 0
      ), 0);
      // The proxy returns only offers available to this customer. Carry its
      // eligibility marker so Shopify can discount once-per-customer gifts.
      if (quantity > existing) pending.push({ id, quantity: quantity - existing, properties: {
        __gift_offer_id: offerId, __is_free_gift: '1', _free_gift: '1', __one_time_offer_ids: offerId,
      } });
    }
    return pending;
  }

  async function prepareForm(formData, root) {
    const panel = findPanel(root);
    if (!panel) return formData;
    await panel.ready;
    if (panel.hidden || !panel.canRedeem) return formData;
    const item = { id: formData.get('id'), quantity: Number(formData.get('quantity') || 1), properties: {} };
    if (formData.get('selling_plan')) item.selling_plan = formData.get('selling_plan');
    for (const [key, value] of formData) {
      const property = key.match(/^properties\[([^\]]+)\]$/);
      if (property) item.properties[property[1]] = value;
    }
    const items = await prepareItems([item], root);
    const body = new FormData();
    for (const [key, value] of formData) {
      if (!['id', 'quantity', 'selling_plan'].includes(key) && !key.startsWith('properties[')) body.append(key, value);
    }
    items.forEach((line, index) => {
      body.append(`items[${index}][id]`, line.id);
      body.append(`items[${index}][quantity]`, line.quantity);
      if (line.selling_plan) body.append(`items[${index}][selling_plan]`, line.selling_plan);
      Object.entries(line.properties || {}).forEach(([key, value]) => body.append(`items[${index}][properties][${key}]`, value));
    });
    return body;
  }

  window.CPBuyBoxGifts = { prepareItems, prepareForm };
  customElements.define('cp-buybox-gifts', BuyboxGifts);
})();
